import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';
import { pricingPlans } from '../lib/stripe.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { Switch } from '../components/ui/switch.jsx';

const PricingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { openSignUp } = useClerk();
  const { tier: currentTier, hasActiveSubscription } = useSubscription();
  const [isYearly, setIsYearly] = useState(searchParams.get('billing') === 'yearly');
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const autoCheckoutTriggered = useRef(false);

  // Auto-trigger checkout if plan is specified in URL
  useEffect(() => {
    const planParam = searchParams.get('plan');
    const billingParam = searchParams.get('billing');

    if (planParam && isLoaded && !autoCheckoutTriggered.current) {
      const planId = planParam === 'premium_plus' ? 'premium_plus' : planParam;
      const plan = pricingPlans.find(p => p.id === planId);

      if (plan) {
        if (billingParam === 'yearly') {
          setIsYearly(true);
        }

        if (isSignedIn) {
          autoCheckoutTriggered.current = true;
          setSearchParams({});
          setTimeout(() => {
            handleSubscribe(plan);
          }, 100);
        } else {
          openSignUp({ redirectUrl: window.location.href });
        }
      }
    }
  }, [searchParams, isLoaded, isSignedIn]);

  const calculateYearlyDiscount = (monthlyPrice, yearlyPrice) => {
    const yearlyTotal = monthlyPrice * 12;
    const discount = ((yearlyTotal - yearlyPrice) / yearlyTotal) * 100;
    return Math.round(discount);
  };

  const handleSubscribe = async (plan) => {
    setError(null);

    if (!isSignedIn) {
      openSignUp({ redirectUrl: window.location.href });
      return;
    }

    if (currentTier === plan.id && hasActiveSubscription) {
      setError('You are already subscribed to this plan.');
      return;
    }

    setLoading(plan.id);

    try {
      const authToken = await getToken();
      const priceId = isYearly ? plan.yearlyPriceId : plan.monthlyPriceId;

      if (!priceId) {
        throw new Error('Price ID not configured. Please contact support.');
      }

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          priceId,
          userEmail: user.primaryEmailAddress?.emailAddress,
          planId: plan.id,
          billingInterval: isYearly ? 'yearly' : 'monthly',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoading('manage');
    try {
      const authToken = await getToken();
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.message || 'Failed to open billing portal. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <section className="py-12 bg-[#f7f7f7] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center mb-4">Choose Your Plan</h2>
        <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
          Unlock the full potential of mindful storytelling
        </p>

        <div className="flex justify-center items-center mb-8">
          <span className={`mr-3 ${isYearly ? 'text-gray-500' : 'font-semibold'}`}>Monthly</span>
          <Switch checked={isYearly} onCheckedChange={setIsYearly} />
          <span className={`ml-3 ${isYearly ? 'font-semibold' : 'text-gray-500'}`}>
            Yearly
            <span className="ml-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">Save up to 17%</span>
          </span>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 max-w-2xl mx-auto flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {pricingPlans.some((plan) => !plan.monthlyPriceId || !plan.yearlyPriceId) && (
          <div className="bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded mb-6 max-w-2xl mx-auto" role="status">
            <strong>Subscriptions are being configured.</strong>{' '}
            Story creation remains available on the free tier; paid checkout will appear here once billing is ready.
          </div>
        )}

        {hasActiveSubscription && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-6 max-w-2xl mx-auto">
            You are currently subscribed to the <strong>{currentTier}</strong> plan.{' '}
            <button
              onClick={handleManageSubscription}
              disabled={loading === 'manage'}
              className="underline hover:no-underline"
            >
              {loading === 'manage' ? 'Loading...' : 'Manage Subscription'}
            </button>
          </div>
        )}

        <div className="px-0 md:px-[5%] lg:px-[10%]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 justify-center">
            {pricingPlans.map((plan, index) => {
              const isCurrentPlan = currentTier === plan.id && hasActiveSubscription;
              const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
              const originalYearlyPrice = plan.monthlyPrice * 12;
              const configuredPriceId = isYearly ? plan.yearlyPriceId : plan.monthlyPriceId;

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-lg p-6 shadow-lg flex flex-col justify-between w-full max-w-sm mx-auto md:max-w-[300px] relative
                    ${plan.popular
                      ? 'border-2 border-blue-500 md:transform md:scale-105 shadow-xl'
                      : 'border border-gray-200 hover:shadow-xl transition-shadow duration-300'
                    } shadow-blue-600/20`}
                >
                  {plan.popular && (
                    <div className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-3 mx-auto absolute left-1/2 transform -translate-x-1/2 -top-3">
                      MOST POPULAR
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-center">{plan.name}</h3>
                    <div className="mb-3 text-center">
                      {isYearly && (
                        <span className="text-gray-500 line-through text-sm mr-1">
                          ${originalYearlyPrice.toFixed(2)}
                        </span>
                      )}
                      <span className="text-2xl font-bold">
                        ${price.toFixed(2)}
                      </span>
                      <span className="text-gray-500 text-sm">/{isYearly ? 'Year' : 'Month'}</span>
                    </div>
                    {isYearly && (
                      <p className="text-xs text-blue-600 mb-3 text-center">
                        Save {calculateYearlyDiscount(plan.monthlyPrice, plan.yearlyPrice)}% with yearly billing
                      </p>
                    )}
                    <ul className="space-y-2 mb-4 text-sm">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          {typeof feature === 'string' ? (
                            <>
                              <svg className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>{feature}</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span className="text-gray-500">{feature.text}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => handleSubscribe(plan)}
                    disabled={loading === plan.id || isCurrentPlan || !configuredPriceId}
                    className={`w-full py-2 px-4 rounded text-sm transition-colors ${
                      isCurrentPlan || !configuredPriceId
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : plan.popular
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-white text-blue-500 border border-blue-500 hover:bg-blue-600 hover:text-white'
                    }`}
                  >
                    {loading === plan.id ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : !configuredPriceId ? (
                      'Coming Soon'
                    ) : (
                      'Get Started'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          All plans include a 7-day free trial. Cancel anytime.
        </p>

        <div className="text-center mt-6">
          <Link to="/" className="text-blue-500 hover:underline text-sm">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingPage;
